import React, { useEffect, useState } from 'react';
import { Download, Monitor, CheckCircle, Info, Share } from 'lucide-react';
import { Button } from './Button';
import { Tooltip } from './Tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './Dialog';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function InstallPWA() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);

  // iOS/iPadOS Safari never fires `beforeinstallprompt`, so the only way to
  // install there is the manual Share -> Add to Home Screen flow. Detect it so
  // the guidance modal shows the right steps (incl. iPadOS, which reports as Mac).
  const isIOS =
    typeof navigator !== 'undefined' &&
    (/iphone|ipad|ipod/i.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));
  const isAndroid = typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent);

  useEffect(() => {
    // Check if app is running in standalone mode (already installed & opened as PWA)
    const checkStandalone = () => {
      const isStandaloneMode =
        window.matchMedia('(display-mode: standalone)').matches ||
        (navigator as unknown as { standalone?: boolean }).standalone ||
        document.referrer.includes('android-app://');
      setIsStandalone(isStandaloneMode);
    };

    checkStandalone();

    // The event may have been captured before this component mounted (see
    // main.tsx). Pick up anything already stashed on window first.
    const existing = (window as unknown as { __installPrompt?: BeforeInstallPromptEvent }).__installPrompt;
    if (existing) setDeferredPrompt(existing);

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      (window as any).__installPrompt = e;
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    // Fired by main.tsx when it captures the event early.
    const handleStashed = () => {
      const p = (window as unknown as { __installPrompt?: BeforeInstallPromptEvent }).__installPrompt;
      if (p) setDeferredPrompt(p);
    };
    const handleInstalled = () => {
      setDeferredPrompt(null);
      setIsStandalone(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('pwa:installprompt', handleStashed);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('pwa:installprompt', handleStashed);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        (window as any).__installPrompt = null;
      }
    } else {
      // No native prompt available (iOS always, or Android/desktop before the
      // browser's engagement heuristic fires) — show platform-specific steps.
      setShowGuideModal(true);
    }
  };

  // If already installed and opened as standalone desktop app, show subtle installed badge or nothing
  if (isStandalone) {
    return (
      <Tooltip label="TeamOne Desktop App (Running Native)" side="bottom">
        <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-success bg-success/10 border border-success/20 rounded-full">
          <CheckCircle className="size-3.5" /> App Installed
        </span>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip label="Install TeamOne Desktop App" side="bottom">
        <Button
          variant="outline"
          size="sm"
          onClick={handleInstallClick}
          className="h-8 px-2 sm:px-3 gap-1.5 border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 hover:border-primary/50 text-xs font-semibold shrink-0"
          aria-label="Install App"
        >
          <Download className="size-3.5" />
          <span className="hidden sm:inline">Install App</span>
        </Button>
      </Tooltip>

      {/* Guidance Modal when browser native prompt is pending */}
      <Dialog open={showGuideModal} onOpenChange={setShowGuideModal}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-md p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <Monitor className="size-5 text-primary" />
              Install TeamOne App
            </DialogTitle>
            <DialogDescription>
              Install TeamOne as a standalone application on your device for instant access and offline support.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {isIOS ? (
              <div className="rounded-lg border border-border bg-surface-2 p-3.5 text-xs text-muted-foreground space-y-2">
                <p className="font-semibold text-foreground flex items-center gap-1.5">
                  <Share className="size-4 text-primary shrink-0" />
                  Install on iPhone / iPad (Safari):
                </p>
                <ol className="list-decimal list-inside space-y-1.5 pl-1">
                  <li>Tap the <strong>Share</strong> button (square with an up arrow) in Safari&apos;s toolbar.</li>
                  <li>Scroll down and tap <strong>Add to Home Screen</strong>.</li>
                  <li>Tap <strong>Add</strong> &mdash; TeamOne appears as an app icon on your home screen.</li>
                </ol>
              </div>
            ) : isAndroid ? (
              <div className="rounded-lg border border-border bg-surface-2 p-3.5 text-xs text-muted-foreground space-y-2">
                <p className="font-semibold text-foreground flex items-center gap-1.5">
                  <Info className="size-4 text-primary shrink-0" />
                  Install on Android (Chrome):
                </p>
                <ol className="list-decimal list-inside space-y-1.5 pl-1">
                  <li>Open the browser menu (<strong>⋮</strong>, top-right).</li>
                  <li>Tap <strong>Install app</strong> (or <strong>Add to Home screen</strong>).</li>
                  <li>Confirm <strong>Install</strong> &mdash; TeamOne appears on your home screen.</li>
                </ol>
                <p className="text-[11px] pt-1">Tip: use <strong>Chrome</strong>. Some in-app browsers (inside WhatsApp, Instagram, etc.) and Firefox can&apos;t install web apps.</p>
              </div>
            ) : (
              <div className="rounded-lg border border-border bg-surface-2 p-3.5 text-xs text-muted-foreground space-y-2">
                <p className="font-semibold text-foreground flex items-center gap-1.5">
                  <Info className="size-4 text-primary shrink-0" />
                  How to install on desktop:
                </p>
                <ol className="list-decimal list-inside space-y-1.5 pl-1">
                  <li>Look at the browser address bar (top right).</li>
                  <li>Click the <strong>Install icon (⊕ / 💻)</strong>, or open the menu (<strong>⋮</strong>) &rarr; <strong>Install TeamOne</strong>.</li>
                  <li>Use <strong>Chrome</strong> or <strong>Edge</strong> — Safari and Firefox on desktop don&apos;t support install.</li>
                </ol>
              </div>
            )}

            {import.meta.env.DEV && (
              <div className="rounded-lg border border-border bg-card p-3 text-xs text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Testing in Development Mode (`npm run dev`)?</p>
                <p>The service worker is disabled in dev — run `npm run build &amp;&amp; npm run preview`, or open the deployed site, for the native install prompt.</p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <Button variant="secondary" size="sm" onClick={() => setShowGuideModal(false)}>
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
